import { useEffect, useState } from 'react';
import isDefined from '@afosto/utils/isDefined';
import { channels as channelsApi } from '@afosto/catalog/api/Channels';
import { methods as methodsApi } from '@afosto/fulfilment/api/Methods';

const useConfiguration = ({ app, integration }) => {
  const [formattedConfiguration, setFormattedConfiguration] = useState({});

  useEffect(() => {
    let isMounted = true;

    const formatConfiguration = async () => {
      try {
        const configuration = { ...(integration?.configuration ?? {}) };

        if (app?.code === 'bol') {
          if (configuration?.channel_id) {
            const response = await channelsApi
              .viewChannel(configuration.channel_id)
              .catch(() => {});
            const { data: channel } = response || {};

            configuration.channel_id = channel?.name || configuration.channel_id;
          }

          const shippingMethods = isDefined(configuration?.shipping_methods)
            ? JSON.parse(atob(configuration.shipping_methods))
            : [];

          const promises = shippingMethods.map(method =>
            methodsApi.getShippingMethod(method.afosto_method_id),
          );
          const responses = await Promise.allSettled(promises).then(result =>
            result.filter(promise => promise.status === 'fulfilled'),
          );
          const formattedMethods = responses
            .map(response => response.value.data)
            .reduce((acc, method) => ({ ...acc, [method.id]: method.name }), {});

          configuration.shipping_methods = shippingMethods.map(
            method =>
              `${method.bol_transporter_code}: ${
                formattedMethods[method.afosto_method_id] || method.afosto_method_id
              }`,
          );

          if (configuration?.shipment_method_id) {
            const methodName = formattedMethods[configuration.shipment_method_id];

            if (methodName) {
              configuration.shipment_method_id = methodName;
            } else {
              const response = await methodsApi
                .getShippingMethod(configuration.shipment_method_id)
                .catch(() => {});
              const { data: method } = response || {};

              configuration.shipment_method_id = method?.name || configuration.shipment_method_id;
            }
          }
        }

        if (app?.code === 'afosto') {
          const channels = isDefined(configuration?.channels)
            ? JSON.parse(atob(configuration.channels))
            : [];

          const promises = channels.map(channel => channelsApi.viewChannel(channel.channel_id));
          const responses = await Promise.allSettled(promises).then(result =>
            result.filter(promise => promise.status === 'fulfilled'),
          );
          const formattedChannels = responses
            .map(response => response.value.data)
            .reduce((acc, channel) => ({ ...acc, [channel.id]: channel.name }), {});

          configuration.channels = channels.map(
            channel =>
              `${formattedChannels[channel.channel_id] || channel.channel_id}: ${
                channel.external_service_id
              }`,
          );

          const shippingMethods = isDefined(configuration?.shipping_methods)
            ? JSON.parse(atob(configuration.shipping_methods))
            : [];

          const shippingMethodPromises = shippingMethods.map(method =>
            methodsApi.getShippingMethod(method.shipping_method_id),
          );
          const shippingMethodResponses = await Promise.allSettled(
            shippingMethodPromises,
          ).then(result => result.filter(promise => promise.status === 'fulfilled'));
          const formattedMethods = shippingMethodResponses
            .map(response => response.value.data)
            .reduce((acc, method) => ({ ...acc, [method.id]: method.name }), {});

          configuration.shipping_methods = shippingMethods.map(
            method =>
              `${method.af_shipping_method_id}: ${
                formattedMethods[method.shipping_method_id] || method.shipping_method_id
              }`,
          );
        }

        if (isMounted) {
          setFormattedConfiguration(configuration);
        }
      } catch (err) {
        if (isMounted) {
          setFormattedConfiguration({});
        }
      }
    };

    if (app && integration) {
      formatConfiguration();
    }

    return () => {
      isMounted = false;
    };
  }, [app, integration]);

  return formattedConfiguration;
};

export default useConfiguration;
